import { ReactNode, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { AlertTriangle, Check, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

/** Estado do salvamento, dirigido pela request real do formulário. */
export type SavingStatus = "saving" | "success" | "error";

export interface SavingStepConfig {
  id: string;
  label: string;
  icon?: ReactNode;
}

interface SavingProgressDialogProps {
  open: boolean;
  /** Passos exibidos como "sendo salvos" — normalmente as seções do formulário. */
  steps: SavingStepConfig[];
  /** Estado real da request: a animação pace os passos, mas só conclui quando isto muda. */
  status: SavingStatus;
  title: string;
  description?: string;
  successTitle?: string;
  successDescription?: string;
  errorTitle?: string;
  errorDescription?: string;
  /** Rótulo do botão de fechar no estado de erro. */
  errorActionLabel?: string;
  /** Disparado depois que a animação de sucesso termina — o caller navega/encerra aqui. */
  onComplete?: () => void;
  /** Disparado quando o usuário fecha o diálogo no estado de erro. */
  onClose?: () => void;
  /** Quanto cada passo "demora" na simulação (ms). */
  stepDurationMs?: number;
}

const DEFAULT_STEP_DURATION = 650;
/**
 * Quanto o estado de sucesso fica visível antes de `onComplete` (navegação/
 * fechamento). Folga generosa (~1.9s) para o usuário ver o resultado — os ✓ de
 * todos os passos e o título de sucesso — antes de a tela mudar.
 */
const SUCCESS_HOLD_MS = 1900;

/**
 * Casca genérica de "salvando…": um diálogo que simula o salvamento das seções
 * de um formulário, marcando-as uma a uma enquanto a request real roda em
 * segundo plano. Agnóstica de domínio — recebe os passos e o `status` de fora.
 *
 * Paceamento: enquanto `status === 'saving'` os passos avançam no tempo, mas o
 * último fica "em andamento" (spinner) sem concluir — assim a simulação nunca
 * termina antes da request. Quando `status` vira `success`, todos os passos são
 * concluídos, mostra-se o estado de sucesso e, após um respiro, chama `onComplete`.
 */
export function SavingProgressDialog({
  open,
  steps,
  status,
  title,
  description,
  successTitle = "Tudo pronto!",
  successDescription,
  errorTitle = "Algo deu errado",
  errorDescription = "Não foi possível concluir. Tente novamente.",
  errorActionLabel = "Fechar",
  onComplete,
  onClose,
  stepDurationMs = DEFAULT_STEP_DURATION,
}: SavingProgressDialogProps) {
  const prefersReducedMotion = useReducedMotion();

  // Índice do passo "em andamento". Avança no tempo enquanto salva; ao concluir
  // (sucesso) é jogado além do último para marcar todos como ✓.
  const [activeIndex, setActiveIndex] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const isSuccess = status === "success";
  const isError = status === "error";

  // Reinicia ao (re)abrir, para uma nova tentativa começar do zero.
  useEffect(() => {
    if (open) setActiveIndex(0);
  }, [open]);

  // Avança os passos no tempo, parando no último enquanto a request não resolve.
  useEffect(() => {
    if (!open || status !== "saving") return;
    if (activeIndex >= steps.length - 1) return;

    const timer = setTimeout(() => setActiveIndex((i) => i + 1), stepDurationMs);
    return () => clearTimeout(timer);
  }, [open, status, activeIndex, steps.length, stepDurationMs]);

  // Sucesso: conclui todos os passos e, após um respiro, devolve o controle ao caller.
  useEffect(() => {
    if (!open || !isSuccess) return;
    setActiveIndex(steps.length);
    const timer = setTimeout(() => onCompleteRef.current?.(), SUCCESS_HOLD_MS);
    return () => clearTimeout(timer);
  }, [open, isSuccess, steps.length]);

  const completedCount = isSuccess ? steps.length : activeIndex;
  const progress = steps.length ? (completedCount / steps.length) * 100 : 0;

  return (
    <Dialog open={open}>
      <DialogContent
        // Sem botão de fechar e sem fechar por fora/Esc: o fluxo é dirigido pelo status.
        className="overflow-hidden sm:max-w-[460px] [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Barra de progresso no topo */}
        <div className="absolute inset-x-0 top-0 h-1 bg-muted">
          <motion.div
            className={cn("h-full", isError ? "bg-destructive" : "bg-primary")}
            initial={false}
            animate={{ width: `${isError ? 100 : progress}%` }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.4, ease: "easeOut" }}
          />
        </div>

        <AnimatePresence mode="wait" initial={false}>
          {isError ? (
            <ErrorState
              key="error"
              title={errorTitle}
              description={errorDescription}
              actionLabel={errorActionLabel}
              onClose={onClose}
              prefersReducedMotion={Boolean(prefersReducedMotion)}
            />
          ) : (
            <motion.div
              key="saving"
              initial={prefersReducedMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="pt-2"
            >
              <DialogTitle className="text-lg font-semibold">
                {isSuccess ? successTitle : title}
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm">
                {isSuccess ? (successDescription ?? description) : description}
              </DialogDescription>

              <ul className="mt-5 space-y-1">
                {steps.map((step, index) => {
                  const state: StepState = isSuccess
                    ? "done"
                    : index < activeIndex
                      ? "done"
                      : index === activeIndex
                        ? "active"
                        : "pending";
                  return (
                    <SavingStepRow
                      key={step.id}
                      step={step}
                      state={state}
                      prefersReducedMotion={Boolean(prefersReducedMotion)}
                    />
                  );
                })}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

type StepState = "pending" | "active" | "done";

function SavingStepRow({
  step,
  state,
  prefersReducedMotion,
}: {
  step: SavingStepConfig;
  state: StepState;
  prefersReducedMotion: boolean;
}) {
  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors duration-300",
        state === "active" && "bg-primary/5",
        state === "pending" && "opacity-50",
      )}
    >
      {/* Indicador de status (✓ / spinner / ponto) */}
      <span className="relative flex size-6 shrink-0 items-center justify-center">
        <AnimatePresence mode="wait" initial={false}>
          {state === "done" ? (
            <motion.span
              key="done"
              className="flex size-6 items-center justify-center rounded-full bg-emerald-500 text-white"
              initial={prefersReducedMotion ? false : { scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 22 }}
            >
              <Check className="size-3.5" strokeWidth={3} />
            </motion.span>
          ) : state === "active" ? (
            <motion.span
              key="active"
              initial={prefersReducedMotion ? false : { scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-primary"
            >
              <Loader2 className="size-5 animate-spin" />
            </motion.span>
          ) : (
            <motion.span
              key="pending"
              className="size-2 rounded-full bg-muted-foreground/40"
              initial={false}
              exit={{ opacity: 0 }}
            />
          )}
        </AnimatePresence>
      </span>

      {step.icon && (
        <span
          className={cn(
            "shrink-0 transition-colors duration-300",
            state === "pending" ? "text-muted-foreground" : "text-foreground",
          )}
        >
          {step.icon}
        </span>
      )}

      <span
        className={cn(
          "text-sm transition-colors duration-300",
          state === "done" && "font-medium text-foreground",
          state === "active" && "font-medium text-foreground",
          state === "pending" && "text-muted-foreground",
        )}
      >
        {step.label}
      </span>
    </li>
  );
}

function ErrorState({
  title,
  description,
  actionLabel,
  onClose,
  prefersReducedMotion,
}: {
  title: string;
  description: string;
  actionLabel: string;
  onClose?: () => void;
  prefersReducedMotion: boolean;
}) {
  return (
    <motion.div
      initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center pt-4 text-center"
    >
      <span className="flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertTriangle className="size-6" />
      </span>
      <DialogTitle className="mt-4 text-lg font-semibold">{title}</DialogTitle>
      <DialogDescription className="mt-1 text-sm">{description}</DialogDescription>
      <Button type="button" variant="outline" className="mt-5 w-full" onClick={onClose}>
        {actionLabel}
      </Button>
    </motion.div>
  );
}
